<template>
  <div>
    <!-- Floating AI button — visible on every page when logged in, except in editor mode -->
    <div v-if="isLoggedIn && !editorOpen" class="ai-fab-wrap" :class="{ dark: isDarkMode }">
      <Transition name="ai-fab-menu">
        <div v-if="fabOpen" class="ai-fab-menu">
          <div class="ai-fab-context">
            <i class="fas fa-location-dot"></i>
            <span>{{ pageLabel }}</span>
            <span v-if="supportsTranscript && hasTranscriptContext" class="ai-fab-context-dot" :title="$t('Transcript context available')"></span>
          </div>
          <!-- Transcript-aware actions — only on transcript/video pages -->
          <template v-if="supportsTranscript">
            <button class="ai-fab-item" :disabled="!hasTranscriptContext" :title="!hasTranscriptContext ? $t('Open a transcript first') : ''" @click="handleSummarize">
              <i class="fas fa-wand-magic-sparkles"></i>
              <span>{{ $t('Summarize this file') }}</span>
              <kbd>⇧S</kbd>
            </button>
            <button class="ai-fab-item" :disabled="!hasTranscriptContext" :title="!hasTranscriptContext ? $t('Open a transcript first') : ''" @click="handleAskSingle">
              <i class="fas fa-file-lines"></i>
              <span>{{ $t('Ask about this file') }}</span>
            </button>
            <button class="ai-fab-item primary" @click="handleAskGlobal">
              <i class="fas fa-comments"></i>
              <span>{{ $t('Ask all transcripts') }}</span>
              <kbd>⇧K</kbd>
            </button>
          </template>
          <!-- Non-transcript pages: global + page-help agents -->
          <template v-else>
            <button class="ai-fab-item primary" @click="handleAskGlobal">
              <i class="fas fa-comments"></i>
              <span>{{ $t('Ask all transcripts') }}</span>
              <kbd>⇧K</kbd>
            </button>
            <button class="ai-fab-item" @click="handleAskPageHelp">
              <i class="fas fa-circle-question"></i>
              <span>{{ $t('Help with this page') }}</span>
            </button>
          </template>
        </div>
      </Transition>
      <button
        class="ai-fab"
        :class="{ open: fabOpen }"
        :aria-label="$t('AI Assistant')"
        :title="$t('AI Assistant') + ' · ' + pageLabel"
        @click="fabOpen = !fabOpen"
      >
        <i :class="fabOpen ? 'fas fa-times' : 'fas fa-wand-magic-sparkles'"></i>
      </button>
    </div>

    <!-- Global panels — mounted once at app root so they work from any route -->
    <AiSummarizePanel
      :visible="aiStore.showSummarize"
      :element-id="aiStore.selectedElement?.elementId || ''"
      :file-name="aiStore.selectedElement?.fileName || ''"
      :project-id="aiStore.selectedElement?.projectId || ''"
      :output-id="aiStore.selectedElement?.outputId || ''"
      :secret-id="aiStore.selectedElement?.secretId || ''"
      :page-context="pageCtx"
      @close="aiStore.closeSummarize()"
    />
    <AiAskPanel
      :visible="aiStore.showAsk"
      :element-id="aiStore.selectedElement?.elementId || ''"
      :file-name="aiStore.selectedElement?.fileName || ''"
      :project-id="aiStore.selectedElement?.projectId || ''"
      :output-id="aiStore.selectedElement?.outputId || ''"
      :secret-id="aiStore.selectedElement?.secretId || ''"
      :default-scope="aiStore.askScope"
      :initial-question="aiStore.askInitialQuestion"
      :active-agent="aiStore.activeAgent"
      :page-context="pageCtx"
      @close="aiStore.closeAsk()"
      @openCitation="handleCitation"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useThemeStore } from '@stores/themeStore';
import { useAuthUserStore } from '@stores/auth';
import { useAiToolsStore } from '@stores/aiToolsStore';
import { useDashboardStore } from '@stores/dashboardStore';
import { openWithLoading } from '@utils/openWithLoading';
import { useTranscriptEditor } from '@components/SrtEditor/useTranscriptEditor';
import { getPageContext, isTranscriptContextRoute } from '@utils/aiPageContext';
import AiSummarizePanel from './AiSummarizePanel.vue';
import AiAskPanel from './AiAskPanel.vue';

export default defineComponent({
  name: 'AiToolsGlobal',
  components: { AiSummarizePanel, AiAskPanel },
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const route = useRoute();
    const aiStore = useAiToolsStore();
    const themeStore = useThemeStore();
    const { isDarkMode } = storeToRefs(themeStore);
    const authStore = useAuthUserStore();
    const dashboardStore = useDashboardStore();

    const isLoggedIn = computed(() => !!authStore.user || !!authStore.isAuthenticated);
    const fabOpen = ref(false);

    // Editor mode: the SRT editor owns the screen — hide the floating AI
    // button (Summarize/Ask live in the editor toolbar instead).
    let editorOpen = computed(() => false);
    try {
      const editor: any = useTranscriptEditor?.();
      if (editor?.showSrtEditor) {
        editorOpen = computed(() => !!editor.showSrtEditor.value);
      }
    } catch { /* editor unavailable — FAB stays visible */ }
    watch(editorOpen, (open) => { if (open) fabOpen.value = false; });

    // ── Page-aware context (multi-agent) ──────────────────────────────
    const pageCtx = computed(() => getPageContext(route.path || '', String(route.name || '')));
    const supportsTranscript = computed(() => pageCtx.value.supportsTranscriptContext);
    const pageLabel = computed(() => pageCtx.value.label);

    // Try to resolve current transcript element — ONLY on transcript/video pages
    // to avoid leaking stale dashboard selections onto Orders/Team/etc.
    const resolveCurrentElement = (): any | null => {
      if (!isTranscriptContextRoute(route.path || '', String(route.name || ''))) return null;
      // 1) SRT editor open — use its element
      try {
        const editor: any = useTranscriptEditor?.();
        const el = editor?.currentTranscriptSource?.value?.element || editor?.baseElement?.value;
        if (el?.SK) {
          return {
            elementId: String(el.SK).split('#')[1] || el.elementId || '',
            fileName: el.fileName || '',
            projectId: el.projectId || dashboardStore.selectedProjectId || '',
            outputId: el.PK ? String(el.PK).split('#')[1] : '',
            secretId: el.secretId || '',
          };
        }
      } catch { /* no usable element here — fall through to dashboard selection below */ }
      // 2) Dashboard selection — only when actually on transcript dashboard
      const sel = (dashboardStore as any).selectedOutputElement || (dashboardStore as any).selectedInputElement;
      if (sel?.SK) {
        return {
          elementId: String(sel.SK).split('#')[1] || sel.elementId || '',
          fileName: sel.fileName || '',
          projectId: dashboardStore.selectedProjectId || sel.projectId || '',
          outputId: sel.PK ? String(sel.PK).split('#')[1] : '',
          secretId: sel.secretId || '',
        };
      }
      // 3) Fallback: prediction element of selected input
      const input = (dashboardStore as any).selectedInput;
      if (input?.SK) {
        const inputId = String(input.SK).split('#')[1];
        const outputs = (dashboardStore as any).outputs?.[inputId] || [];
        for (const out of outputs) {
          const els = (dashboardStore as any).outputElements?.[out.SK.split('#')[1]] || [];
          const pred = els.find((e: any) => e.fileName === 'prediction.json' && e.secretId);
          if (pred) {
            return {
              elementId: String(pred.SK).split('#')[1] || pred.elementId || '',
              fileName: pred.fileName || input.name || '',
              projectId: dashboardStore.selectedProjectId || '',
              outputId: String(pred.PK).split('#')[1] || '',
              secretId: pred.secretId || '',
            };
          }
        }
      }
      return null;
    };

    const hasTranscriptContext = computed(() => !!resolveCurrentElement());

    // Clear stale single-file scope when navigating away from transcript pages
    watch(() => route.path, (newPath, oldPath) => {
      const wasTranscript = isTranscriptContextRoute(oldPath || '', '');
      const nowTranscript = isTranscriptContextRoute(newPath || '', String(route.name || ''));
      if (wasTranscript && !nowTranscript) {
        aiStore.clearStaleTranscriptContext();
      }
    });

    const handleSummarize = () => {
      fabOpen.value = false;
      if (!supportsTranscript.value) {
        // On non-transcript pages, summarize has no file context — open empty
        aiStore.openSummarize(null);
        return;
      }
      const ctx = resolveCurrentElement();
      aiStore.openSummarize(ctx);
    };

    const handleAskSingle = () => {
      fabOpen.value = false;
      if (!supportsTranscript.value) {
        aiStore.openAsk({ scope: 'global', agent: 'global' });
        return;
      }
      const ctx = resolveCurrentElement();
      if (!ctx) {
        aiStore.openAsk({ scope: 'global', agent: 'global' });
        return;
      }
      aiStore.openAsk({ scope: 'single', agent: 'transcript', element: ctx });
    };

    const handleAskGlobal = () => {
      fabOpen.value = false;
      // On transcript pages keep element for citation context; otherwise clear stale
      if (supportsTranscript.value) {
        if (!aiStore.selectedElement) {
          const ctx = resolveCurrentElement();
          if (ctx) aiStore.selectedElement = ctx;
        }
        aiStore.openAsk({ scope: 'global', agent: 'global' });
      } else {
        aiStore.openAsk({ scope: 'global', agent: 'global' });
      }
    };

    const handleAskPageHelp = () => {
      fabOpen.value = false;
      aiStore.openAsk({ scope: 'global', agent: 'page-help', question: `Help me with the ${pageLabel.value} page` });
    };

    const handleCitation = (c: any) => {
      aiStore.closeAsk();
      const eid = c.elementId;
      if (!eid) return;
      // Navigate to dashboard with deep-link; dashboard will open modal + seek
      const query: any = { element: eid };
      if (c.start != null) query.time = String(Math.floor(Number(c.start)));
      // Try to include project if we know it
      const proj = aiStore.selectedElement?.projectId || (dashboardStore as any).selectedProjectId;
      if (proj) query.project = proj;
      void openWithLoading(router, { path: '/Users/transcriptordashboard', query });
    };

    const onKeydown = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod || !e.shiftKey) return;
      if (e.key.toLowerCase() === 'k') {
        e.preventDefault();
        handleAskGlobal();
      } else if (e.key.toLowerCase() === 's') {
        e.preventDefault();
        handleSummarize();
      }
    };

    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.ai-fab-wrap')) fabOpen.value = false;
    };

    onMounted(() => {
      window.addEventListener('keydown', onKeydown);
      window.addEventListener('click', onClickOutside);
    });
    onUnmounted(() => {
      window.removeEventListener('keydown', onKeydown);
      window.removeEventListener('click', onClickOutside);
    });

    return { aiStore, isDarkMode, isLoggedIn, fabOpen, editorOpen, pageCtx, pageLabel, supportsTranscript, hasTranscriptContext, handleSummarize, handleAskSingle, handleAskGlobal, handleAskPageHelp, handleCitation };
  },
});
</script>

<style scoped>
.ai-fab-wrap {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 9998;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
}
.ai-fab {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  color: #fff;
  font-size: 1.25rem;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(79, 70, 229, 0.45), 0 2px 8px rgba(0,0,0,0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}
.ai-fab:hover { transform: translateY(-2px) scale(1.03); box-shadow: 0 12px 32px rgba(79,70,229,0.5); }
.ai-fab.open { background: #1f2937; }
.ai-fab-menu {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: stretch;
  min-width: 220px;
  background: #fff;
  border: 1px solid #e6e9f0;
  border-radius: 14px;
  padding: 8px;
  box-shadow: 0 12px 32px rgba(15,23,42,0.18);
}
.dark .ai-fab-menu { background: #1a2233; border-color: #2a3550; }
.ai-fab-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid transparent;
  background: #f8fafc;
  color: #1f2937;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s, border-color 0.15s;
}
.ai-fab-item:hover { background: #eef2ff; border-color: #c7d2fe; }
.ai-fab-item.primary { background: #4f46e5; color: #fff; }
.ai-fab-item.primary:hover { background: #4338ca; }
.dark .ai-fab-item { background: #212b40; color: #e2e8f0; }
.dark .ai-fab-item:hover { background: #2a3550; border-color: #3a4a6b; }
.dark .ai-fab-item.primary { background: #4f46e5; color: #fff; }
.ai-fab-context { display: flex; align-items: center; gap: 6px; padding: 6px 8px; font-size: 0.75rem; font-weight: 600; opacity: 0.7; border-bottom: 1px solid #e6e9f0; margin: -2px -2px 2px; }
.dark .ai-fab-context { border-color: #2a3550; }
.ai-fab-context-dot { width: 7px; height: 7px; border-radius: 50%; background: #22c55e; margin-left: auto; box-shadow: 0 0 6px rgba(34,197,94,0.6); }
.ai-fab-item:disabled { opacity: 0.45; cursor: not-allowed; }
.ai-fab-item kbd {
  margin-left: auto;
  font-size: 0.65rem;
  opacity: 0.6;
  background: rgba(0,0,0,0.08);
  padding: 2px 6px;
  border-radius: 4px;
}
.ai-fab-item.primary kbd { background: rgba(255,255,255,0.2); }
.ai-fab-menu-enter-active, .ai-fab-menu-leave-active { transition: opacity 0.18s, transform 0.18s; }
.ai-fab-menu-enter-from, .ai-fab-menu-leave-to { opacity: 0; transform: translateY(8px) scale(0.98); }
@media (max-width: 768px) {
  .ai-fab-wrap { right: 14px; bottom: 88px; }
  .ai-fab { width: 52px; height: 52px; }
}
</style>
